import React from 'react';
import { observer } from 'mobx-react';
import { observable, action, runInAction, makeObservable, } from 'mobx';

import { type NoteInfo } from '@root/models/NoteInfo';
import { type CategoryInfo } from '@root/models/CategoryInfo';
import { PromiseCompletion } from '@classes/PromiseCompletion';
import { apiService } from '@services/ApiService';

import CategoryPicker from '@components/CategoryPicker';
import { type IModalDialogContent, type ModalButtonOptions, ModalButtonType, type ModalDialogOptions, ModalWindow } from '@components/Modal';

export type ChangeCategoryDialogProps = {
  note: NoteInfo;
};

export type ChangeCategoryNoteParameters = {
  id: string;
  name: string;
  color?: string;
}

@observer
export default class ChangeCategoryDialog extends React.Component<ChangeCategoryDialogProps> implements IModalDialogContent<ChangeCategoryNoteParameters> {
  @observable private _name: string;
  @observable private _color?: string;
  @observable private _activeCategoryId: string;
  @observable private _categories: CategoryInfo[] = [];
  private _loader: PromiseCompletion = new PromiseCompletion();

  constructor(props: ChangeCategoryDialogProps){
    super(props);
    makeObservable(this);
    this._name = 'New category';
    this._color = '#ffffff';
    // Uncategorized notes start on the virtual Default bucket; '' means "create new".
    this._activeCategoryId = props.note.categoryId ?? 'default';
  }

  public getModalOptions(window: ModalWindow<ChangeCategoryNoteParameters>): ModalDialogOptions<ChangeCategoryNoteParameters> {
    const buttons: ModalButtonOptions<ChangeCategoryNoteParameters>[] = [];
    
    buttons.push({
      type: ModalButtonType.Cancel,
      onClick: () => {
        window.close();
      }
    });

    buttons.push({
      title: 'Move',
      type: ModalButtonType.Save,
      onClick: () => {
        this._onSaveClick(window);
      }
    });

    return {
      title: `Category for "${this._name}"`,
      width: 600,
      buttons: buttons,
      loader: this._loader
    };
  }

  componentDidMount(): void {
    void this._loadData();
  }

  render(){
    return (
      <>
        <div className="form-group">
          <label className="form-control-label">Category</label>
          <CategoryPicker 
            categories={this._categories}
            activeCategoryId={this._activeCategoryId}
            onCategoryChanged={this._onCategoryChanged}
          />
        </div>

        {!this._activeCategoryId && <div className="form-group">
          <label className="form-group-label">New Category Name</label>
          <input className="form-control" type="text" value={this._name} onChange={(e) => this._name = e.target.value} />
        </div>}
      </>
    );
  }

  private async _loadData(){
    const data = await apiService.get<CategoryInfo[]>('/api/note/category', null, {
      loader: this._loader
    });

    // Legacy uncategorized notes can be moved back to the virtual Default bucket.
    if (this.props.note.categoryId === null || this.props.note.categoryId === undefined || this.props.note.categoryId === ''){
      data.push({
        id: 'default',
        name: 'Default',
        sortOrder: 0
      });
    }

    data.push({
      id: '',
      name: 'New category',
      sortOrder: Number.MAX_SAFE_INTEGER
    });
    
    runInAction(() => {
      this._categories = data;
    });
  }
  
  @action.bound
  private async _onSaveClick(window: ModalWindow<ChangeCategoryNoteParameters>){
    // '' means "create a new category"; 'default' means "clear the category" (legacy bucket).
    if (!this._activeCategoryId){
      const categoryData = {
        name: this._name,
        color: this._color
      };

      const newCategory = await apiService.post<CategoryInfo>('/api/note/category', categoryData, {
        loader: this._loader
      });

      this._activeCategoryId = newCategory.id;
    }
    
    const updateCategoryData = { 
      noteId: this.props.note.id, 
      categoryId: this._activeCategoryId === 'default' ? '' : this._activeCategoryId 
    };
    await apiService.put<CategoryInfo>(`/api/note/${this.props.note.id}/category`, updateCategoryData, {
      loader: this._loader
    });

    window.close(ModalButtonType.Create, {
      id: this._activeCategoryId,
      name: this._name,
      color: this._color
    });
  }

  @action.bound
  private _onCategoryChanged(c: CategoryInfo){
    this._activeCategoryId = c.id;
  }
}